import { FormEvent, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useElementVisible from "../hooks/useElementVisible";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import Button from "../components/UI/Button";
import Checkbox from "../components/UI/Checkbox";
import InputWithFloatingButtons from "../components/UI/InputWithFloatingButtons";
import Loader from "../components/UI/Loader";
import DataTable from "../components/UI/DataTable";
import { DataTableColumn } from "../models/DataTable";
import { activateProgram, createProgram, deactivateProgram, deleteProgram, updateProgram } from "../services/programsService";
import Dropdown from "../components/UI/Dropdown";
import Program from "../models/serviceModels/Program";
import Modal from "../components/UI/Modal";
import ErrorMessages from "../components/UI/ErrorMessages";
import Input from "../components/UI/Input";
import useSortableList from "../hooks/useSortableList";

const COLUMNS: DataTableColumn[] = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'totalForms', label: 'Assigned Forms', sortable: true },
    { key: 'totalClients', label: 'Enrolled Participants', sortable: true },
    { key: 'deactivated', label: 'Active', sortable: true },
    { key: 'actions', label: '', sortable: false },
];

const QUERY_CONFIG = {
    take: 10,
    defaultSortBy: 'name',
    entity: 'programs'
};

const ManagePrograms = () => {
    const [query, setQuery] = useState('');
    const [submittedQuery, setSubmittedQuery] = useState('');
    const [includeDeactivated, setIncludeDeactivated] = useState(false);
    const [apiError, setApiError] = useState<any>();
    const [confirmDeleteProgram, setConfirmDeleteProgram] = useState<Program>();
    const [confirmDeactiveProgram, setConfirmDeactiveProgram] = useState<Program>();
    const [unenrollClients, setUnenrollClients] = useState(false);
    const [unenrollClientsDate, setUnenrollClientsDate] = useState<string>();
    const [unenrollClientsReason, setUnenrollClientsReason] = useState<string>();
    const [unassignForms, setUnassignForms] = useState(false);
    const [modalError, setModalError] = useState<any>();
    const [deleting, setDeleting] = useState(false);
    const [deactivating, setDeactivating] = useState(false);
    const [activating, setActivating] = useState(false);
    const [newProgram, setNewProgram] = useState(false);
    const [programName, setProgramName] = useState('');
    const [creating, setCreating] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [programToRename, setRenameToProgram] = useState<Program>();

    const queryClient = useQueryClient();
    const [reference, visible] = useElementVisible<HTMLDivElement>();
    const { data: programs, isLoading, isFetching, sortBy, sortDirection, onSort, resetSort, triggerSearch, fetchNextPage, hasNextPage } = useSortableList<Program>({
        entity: QUERY_CONFIG.entity,
        filter: { query: submittedQuery, includeDeactivated },
        take: QUERY_CONFIG.take,
        defaultSortBy: QUERY_CONFIG.defaultSortBy
    });

    useEffect(() => {
        document.title = "Manage Programs";
    }, []);

    useEffect(() => {
        if (visible && hasNextPage && !isFetching) fetchNextPage();
    }, [visible, hasNextPage, isFetching, fetchNextPage]);

    const handleOnSubmit = (e: FormEvent) => {
        e.preventDefault();
        setSubmittedQuery(query);
        triggerSearch();
    };

    const handleReset = () => {
        setQuery('');
        setSubmittedQuery('');
        resetSort();
    };

    const handleOnRenameProgram = (program: Program) => {
        setModalError(undefined);
        setProgramName('');
        setRenaming(false);
        setRenameToProgram(program);
    };

    const handleOnCancelRenameProgram = () => {
        setModalError(undefined);
        setProgramName('');
        setRenaming(false);
        setRenameToProgram(undefined);
    };

    const handleOnCreateProgram = () => {
        setModalError(undefined);
        setProgramName('');
        setCreating(false);
        setNewProgram(true);
    };

    const handleOnCancelCreateProgram = () => {
        setModalError(undefined);
        setProgramName('');
        setCreating(false);
        setNewProgram(false);
    };

    const handleOnCreate = (e: FormEvent) => {
        e.preventDefault();

        if (!programName) {
            setModalError('Program name is required');
            return;
        }

        setCreating(true);

        createProgram({ name: programName }).then(() => {
            queryClient.invalidateQueries({ queryKey: ['programs'] });
            handleOnCancelCreateProgram();
        }).catch(e => {
            setModalError('Failed to create program');
            console.error(e);
        }).finally(() => {
            setCreating(false);
        });
    };

    const handleOnRename = (e: FormEvent) => {
        e.preventDefault();

        if (!programToRename) {
            console.warn('No program selected to rename');
            return;
        }

        if (!programName) {
            setModalError('Program name is required');
            return;
        }

        setRenaming(true);

        updateProgram({ id: programToRename.id, name: programName }).then(() => {
            queryClient.invalidateQueries({ queryKey: ['programs'] });
            handleOnCancelRenameProgram();
        }).finally(() => {
            setRenaming(false);
        });
    };

    const handleOnCancelDeactivate = () => {
        setConfirmDeactiveProgram(undefined);
        setModalError(undefined);
        setUnassignForms(false);
        setUnenrollClientsDate(undefined);
        setUnenrollClientsReason(undefined);
        setUnenrollClients(false);
        setDeactivating(false);
    };

    const handleOnConfirmDeactivate = (program: Program) => {
        setConfirmDeactiveProgram(program);
        setModalError(undefined);
        setUnassignForms(false);
        setUnenrollClientsDate(undefined);
        setUnenrollClientsReason(undefined);
        setUnenrollClients(false);
        setDeactivating(false);
    };

    const handleOnActivate = (program: Program) => {
        setActivating(true);

        activateProgram(program.id).then(() => {
            queryClient.invalidateQueries({ queryKey: ['programs'] });
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setActivating(false);
        });
    };

    const handleOnDeactivate = () => {
        if (!confirmDeactiveProgram) {
            console.error('Unable to deactivate program. None was selected');
            return;
        }

        if (unenrollClients && !unenrollClientsDate) {
            setModalError('Program exit date is required for unenrolling all participants');
            return;
        }

        setDeactivating(true);

        deactivateProgram({
            programId: confirmDeactiveProgram.id,
            unassignForms,
            unenrollClients,
            unenrollClientsDate,
            unenrollClientsReason
        }).then(() => {
            queryClient.invalidateQueries({ queryKey: ['programs'] });
            handleOnCancelDeactivate();
        }).catch(e => {
            console.error(e);
            setModalError('Failed to deactivate program');
        }).finally(() => {
            setDeactivating(false);
        });
    };

    const handleOnConfirmDelete = (program: Program) => {
        setModalError(undefined);
        setConfirmDeleteProgram(program);
        setDeleting(false);
    };

    const handleOnDelete = async () => {
        if (!confirmDeleteProgram) {
            console.warn('Unable to delete, no program selected');
            return;
        }

        setDeleting(true);

        try {
            await deleteProgram(confirmDeleteProgram.id);
            queryClient.invalidateQueries({ queryKey: ['programs'] });
            setConfirmDeleteProgram(undefined);
        } catch (e) {
            console.error(e);
            setModalError(e);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <section id="main-content" tabIndex={-1}>
            {isLoading && <Loader />}
            {!isLoading && (
                <>
                    <h1>Manage Programs</h1>
                    {activating && <Loader />}
                    <div className="row justify-content-center margin-vertical-2">
                        <div className="column-medium">
                            <ApiErrorMessages error={apiError} />
                            <div className="row justify-content-between align-items-end margin-vertical-4">
                                <div className="column-medium">
                                    <form onSubmit={handleOnSubmit}>
                                        <div className="row">
                                            <div className="column">
                                                <label htmlFor="filter-programs-input-text">Filter Programs</label>
                                                <InputWithFloatingButtons
                                                    id="filter-programs-input"
                                                    placeholder="Search by name"
                                                    value={query}
                                                    onChange={(e) => setQuery(e)}
                                                    buttons={[{ text: 'Reset', onClick: handleReset, className: 'secondary' }]} />
                                            </div>
                                            <div className="column-auto align-self-end">
                                                <Button id="filter-programs-button"
                                                    className="secondary"
                                                    type="submit"
                                                    text="Filter"
                                                    disabled={isFetching}
                                                    loading={isFetching} />
                                            </div>
                                        </div>
                                    </form>
                                </div>
                                <div className="column-auto">
                                    <div className="row align-items-center">
                                        <div className="column-auto">
                                            <Checkbox id="include-deactivated"
                                                checked={includeDeactivated}
                                                containerClassName="margin-top-0 unboxed"
                                                label="Show Deactivated"
                                                onChange={(val) => { setIncludeDeactivated(val); triggerSearch(); }} />
                                        </div>
                                        <div className="column-auto">
                                            <Button id="create-program-button"
                                                text="Create Program"
                                                onClick={handleOnCreateProgram} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {programs.length > 0 && (
                        <div className="row margin-vertical-2 justify-content-center">
                            <div className="column-medium">
                                <div style={{ opacity: isFetching ? 0.5 : 1, transition: 'opacity 0.2s ease' }}>
                                    <DataTable
                                        columns={COLUMNS}
                                        data={programs}
                                        sortBy={sortBy}
                                        sortDirection={sortDirection}
                                        onSort={onSort}
                                        disabled={isFetching}
                                        renderRow={(p: Program) => {
                                            const options: { label: string; href?: string; onClick?: () => void }[] = [
                                                { label: 'Manage Forms', href: `/admin/programs/${p.id}/forms` },
                                                { label: 'Rename', onClick: () => handleOnRenameProgram(p) }
                                            ];

                                            if (p.deactivated) {
                                                options.push({ label: 'Activate', onClick: () => handleOnActivate(p) });
                                            } else {
                                                options.push({ label: 'Deactivate', onClick: () => handleOnConfirmDeactivate(p) });
                                            }

                                            options.push({ label: 'Delete', onClick: () => handleOnConfirmDelete(p) });

                                            return (
                                                <tr key={p.id} className="table-row">
                                                    <td>{p.name}</td>
                                                    <td>{p.totalForms}</td>
                                                    <td>{p.totalClients}</td>
                                                    <td>{!p.deactivated ? 'Yes' : <span className="text-danger">No</span>}</td>
                                                    <td>
                                                        <div className="row justify-content-end">
                                                            <div className="column-auto">
                                                                <Dropdown id={`program-options-${p.id}`}
                                                                    options={options}
                                                                    optionSize="large" />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {programs.length === 0 && !isFetching && (
                        <div className="row justify-content-center margin-vertical-2">
                            <div className="column-auto">
                                <p>No Programs Found</p>
                            </div>
                        </div>
                    )}
                    {hasNextPage && (
                        <div ref={reference} className="padding-5 margin-bottom-5"></div>
                    )}
                </>
            )}

            <Modal id="create-modal" visible={newProgram} className="small" onClose={handleOnCancelCreateProgram} title="Create New Program">
                <div>
                    <form onSubmit={handleOnCreate}>
                        {modalError && <ErrorMessages messages={[modalError]} />}
                        <div className="row">
                            <div className="column">
                                <Input id="new-program-name"
                                    required={true}
                                    onChange={setProgramName}
                                    label="New Program Name" />
                            </div>
                        </div>
                        <div className="row margin-top-5">
                            <div className="column">
                                <div className="row justify-content-between">
                                    <div className="column-medium-auto">
                                        <Button id="cancel-create"
                                            text="Cancel"
                                            className="secondary"
                                            disabled={creating}
                                            onClick={handleOnCancelCreateProgram} />
                                    </div>
                                    <div className="column-medium-auto">
                                        <Button id="confirm-create"
                                            text="Create Program"
                                            className="action"
                                            disabled={creating}
                                            loading={creating}
                                            type="submit" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </Modal>

            <Modal id="rename-modal" visible={programToRename !== undefined} className="small" onClose={handleOnCancelRenameProgram} title="Rename Program">
                {programToRename && (
                    <div>
                        <form onSubmit={handleOnRename}>
                            {modalError && <ErrorMessages messages={[modalError]} />}
                            <div className="row">
                                <div className="column">
                                    <div>Current Name: {programToRename.name}</div>
                                    <Input id="new-program-name"
                                        required={true}
                                        onChange={setProgramName}
                                        label="New Program Name" />
                                </div>
                            </div>
                            <div className="row margin-top-5">
                                <div className="column">
                                    <div className="row justify-content-between">
                                        <div className="column-medium-auto">
                                            <Button id="cancel-rename"
                                                text="Cancel"
                                                className="secondary"
                                                disabled={renaming}
                                                onClick={handleOnCancelRenameProgram} />
                                        </div>
                                        <div className="column-medium-auto">
                                            <Button id="confirm-rename"
                                                text="Rename Program"
                                                className="action"
                                                disabled={renaming}
                                                loading={renaming}
                                                type="submit" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}
            </Modal>

            <Modal id="delete-modal" visible={confirmDeleteProgram !== undefined} className="small" onClose={() => setConfirmDeleteProgram(undefined)} title="Confirm Delete of Program">
                {confirmDeleteProgram && (
                    <div>
                        {modalError && <ErrorMessages messages={[modalError]} />}
                        <div className="row">
                            <div className="column">
                                <div className="text-bold">{confirmDeleteProgram.name}</div>
                                <br />
                                <div>Deleting this program means the following:</div>
                                <ul>
                                    <li>Program enrollment history data is <span className="text-bold">deleted</span> (e.g. no record that J.J. Jones was ever enrolled)</li>
                                    <li>All tracking forms for this program will be <span className="text-bold">unassigned</span>.</li>
                                    <li>Those unassigned forms will be <span className="text-bold">kept</span> to use again.</li>
                                    <li>All data submitted through these tracking forms will be <span className="text-bold">kept</span> for reporting.</li>
                                </ul>
                                <br />
                                <div>Are you sure you want to delete this program?</div>
                                <div className="text-bold">The change is irreversible.</div>
                            </div>
                        </div>
                        <div className="row margin-top-5">
                            <div className="column">
                                <div className="row justify-content-between">
                                    <div className="column-medium-auto">
                                        <Button id="cancel-delete"
                                            text="Cancel"
                                            className="secondary"
                                            disabled={deleting}
                                            onClick={() => setConfirmDeleteProgram(undefined)} />
                                    </div>
                                    <div className="column-medium-auto">
                                        <Button id="confirm-delete"
                                            text="Delete Program"
                                            className="warning"
                                            disabled={deleting}
                                            loading={deleting}
                                            onClick={handleOnDelete} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal id="deactivate-modal" visible={confirmDeactiveProgram !== undefined} className="small" onClose={() => setConfirmDeactiveProgram(undefined)} title="Confirm Deactivate of Program">
                {confirmDeactiveProgram && (
                    <div>
                        {modalError && <ErrorMessages messages={[modalError]} />}
                        <div className="row">
                            <div className="column">
                                <div className="text-bold">{confirmDeactiveProgram.name}</div>
                                {confirmDeactiveProgram?.totalClients && confirmDeactiveProgram.totalClients > 0 && <>
                                    <br />
                                    <div>Total Enrolled Participants: {confirmDeactiveProgram.totalClients}</div>
                                    <div>
                                        <Checkbox id="unenroll-clients"
                                            checked={unenrollClients}
                                            disabled={deactivating}
                                            onChange={setUnenrollClients}
                                            label="Unenroll ALL Participants" />
                                    </div>
                                    {unenrollClients && <>
                                        <br />
                                        <div>
                                            <Input id="unenroll-clients-date"
                                                value={unenrollClientsDate}
                                                type="date"
                                                required={true}
                                                onChange={setUnenrollClientsDate}
                                                disabled={deactivating || !unenrollClients}
                                                label="Program Exit Date" />
                                        </div>
                                        <br />
                                        <div>
                                            <Input id="unenroll-clients-reason"
                                                value={unenrollClientsReason}
                                                type="multi"
                                                onChange={setUnenrollClientsReason}
                                                disabled={deactivating || !unenrollClients}
                                                label="Program Exit Reason (optional)" />
                                        </div>
                                    </>}
                                </>}
                                {confirmDeactiveProgram?.totalForms && confirmDeactiveProgram.totalForms > 0 && <>
                                    <br />
                                    <div>Total Assigned Forms: {confirmDeactiveProgram.totalForms}</div>
                                    <div>
                                        <Checkbox id="unassign-forms"
                                            checked={unassignForms}
                                            disabled={deactivating}
                                            onChange={setUnassignForms}
                                            label="Unassign ALL Forms" />
                                    </div>
                                </>}
                                <br />
                                <div>Are you sure you want to deactivate this program?</div>
                            </div>
                        </div>
                        <div className="row margin-top-5">
                            <div className="column">
                                <div className="row justify-content-between">
                                    <div className="column-medium-auto">
                                        <Button id="cancel-deactivate"
                                            text="Cancel"
                                            className="secondary"
                                            disabled={deactivating}
                                            onClick={handleOnCancelDeactivate} />
                                    </div>
                                    <div className="column-medium-auto">
                                        <Button id="confirm-deactivate"
                                            text="Deactivate Program"
                                            className="warning"
                                            disabled={deactivating}
                                            loading={deactivating}
                                            onClick={handleOnDeactivate} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </section>
    );
};

export default ManagePrograms;
